import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon/Icon';
import styles from './NotificationCard.module.scss';

export type NotificationCardVariant = 'success' | 'info' | 'warning' | 'error';

export type NotificationCardProps = {
  variant?: NotificationCardVariant;
  icon?: string;
  title: string;
  content: string;
  onClick?: () => void;
  testId: string;
};

const VARIANT_KEYS: Record<NotificationCardVariant, string> = {
  success: 'notifications.variant.success',
  info: 'notifications.variant.info',
  warning: 'notifications.variant.warning',
  error: 'notifications.variant.error',
};

export const NotificationCard: React.FC<NotificationCardProps> = ({
  variant = 'success',
  icon,
  title,
  content,
  onClick,
  testId,
}) => {
  const { t } = useTranslation();
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;
  const isInteractive = !!onClick;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ((event.key === 'Enter' || event.key === ' ') && onClick) {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={`${styles['notification-card']} ${styles[variant]}`}
      onClick={onClick}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      data-testid={testId}
    >
      <span className={styles['sr-only']}>{t(VARIANT_KEYS[variant])}:</span>
      {icon && (
        <div className={styles['avatar']} aria-hidden="true">
          <Icon name={icon} size={30} />
        </div>
      )}
      <div className={styles['content']}>
        <div className={styles['text']}>
          <h3 id={titleId} className={styles['title']}>
            {title}
          </h3>
          <p id={descriptionId} className={styles['description']}>
            {content}
          </p>
        </div>
      </div>
      {isInteractive && (
        <div className={styles['chevron-wrapper']} aria-hidden="true">
          <Icon name="chevron-right" size={24} />
        </div>
      )}
    </div>
  );
};

export default NotificationCard;
